<script>
    import ibTableBasic from './ib-table-basic.vue';
    import ibTableCol from './ib-table-col.vue';
    import chevron_round_right from '../../icon/arrow/chevron_round_right.svg';

    /**
     * Extension of [ib-table-basic](#/components/table/ib-table-basic) with row groups that can collapse or expand.
     */
    export default {
        name: 'ib-table-group',

        mixins: [
            ibTableBasic
        ],

        props: {
            /**
             * An array of currently expanded table rows by id.
             */
            value: [String,Number,Array],
            /**
             * Member of data object to use as emitted value, or function to retrieve such. This is useful if using
             * v-model to control expanded rows.
             */
            emit: [String,Number,Function],
            /**
             * Member of data object to use as child object, or function to retrieve such. If value is falsy, row will
             * not be expandable. If value is array, an array of child rows will be rendered.
             */
            child: [String,Number,Function],
            /**
             * If true, allow multiple rows to be expanded simultaneously.
             */
            multiple: Boolean,
            /**
             * Click action. `none` is supported; `end` is supported to open end slot; default is `expand` to expand
             * current row.
             */
            click: {
                type: String,
                default: 'expand'
            }
        },

        data() {
            return {
                ivalue: new Set(),
                ivaluer: 0  //vue2
            };
        },

        computed: {
            getchild() {
                const child = this.child;
                if (typeof(child) === 'function') {
                    return child;
                } else if (child != null) {
                    return row => row[child];
                } else {
                    return row => row;
                }
            },

            lclick() {
                return this.click != 'none' || !!this.$listeners.click;
            }
        },

        watch: {
            value: {
                handler(value) {
                    if (value == null) {
                        this.ivalue.clear();
                    } else {
                        this.ivalue = new Set(Array.isArray(value) ? value : [value]);
                    }
                },
                immediate: true
            }
        },

        methods: {
            renderHeadBefore(h) {
                return [
                    h(ibTableCol, {
                        'staticClass': '_tbsid',
                        'attrs': { sticky: 'start', width: '20px', force: true }
                    })];
            },

            renderBody(h) {
                void this.ivaluer; //vue2
                const data = this.data;
                const body = this.renderSlot('body');
                const schild = this.renderSlot('child', body);
                const renderRow = this.renderRow;
                const lclick = !!this.$listeners.click;
                const setCsv = this.setCsv;
                this.csv = '';

                return this.index.length
                    ? this.index.reduce((el,row) => {
                        const id = row.id;
                        const rdata = data[row.index];
                        let children = this.getchild(rdata);
                        if (!Array.isArray(children)) {
                            children = children ? [children] : [];
                        }
                        el.push(h('tbody', {
                            'class': this.ivalue.has(id) && children.length ? '_tbgex' : null
                        }, [
                            renderRow(h, id, [
                                ...this.renderBodyStart(h),
                                h('td', {
                                    'staticClass': '_tbsid mirror-rtl'
                                }, children.length ? [
                                    h('a', {
                                        'attrs': { role: 'button' },
                                        'staticClass': '_tbga',
                                        'on': { click: this.expandClick },
                                        'domProps': {
                                            'innerHTML': chevron_round_right
                                        }
                                    })
                                ] : void 0),
                                body(rdata, setCsv),
                                ...this.renderBodyEnd(h, id, rdata)
                            ], {
                                '_tbgr': true,
                                '_trn': !lclick && !children.length
                            })
                        ]));
                        if (children.length) {
                            el.push(h('tbody', {
                                'staticClass': '_tbgch'
                            }, this._l(children, (child,index) => {
                                const id = row.id + '::' + index;
                                return renderRow(h, id, [
                                    ...this.renderBodyStart(h),
                                    h('td', {
                                        'staticClass': '_tbsid _tbchid'
                                    }),
                                    schild(child, setCsv),
                                    ...this.renderBodyEnd(h, id, child)
                                ], {
                                    '_trn': !lclick
                                });
                            })));
                        }
                        return el;
                    }, [])

                    : h('tbody', {
                        'staticClass': '_tbb'
                    }, [
                        this.renderEmpty(h)
                    ])
            },

            expandClick(e) {
                this.onclick(e, 'expand');
                e.stopPropagation();
            },

            /** @private */
            canhover(id) {
                if (this.$listeners.click || this.slotStart || this.slotEnd) {
                    return true;
                } else {
                    const row = this.getrow(id);
                    return !!(row && this.getchild(row.data));
                }
            },

            /** @private */
            clickhook(id, action) {
                if (this.click == 'expand' || action == 'expand') {
                    // retrieve id in original type
                    const row = this.getrow(id);
                    if (row != null) {
                        const ivalue = this.ivalue;
                        if (ivalue.has(row.id)) {
                            ivalue.delete(row.id);
                            this.$emit('shrink', row.data);
                        } else {
                            ivalue.add(row.id)
                            this.$emit('expand', row.data);
                        }
                        this.$emit('input', [...ivalue]);
                        this.ivaluer++;   //vue2
                    }
                }
            },

            /** @private */
            clickemit(id, ci) {
                id = id.split('::');
                const row = this.getrow(id[0]);
                if (row != null) {
                    let data;
                    if (id.length > 1) {
                        let children = this.getchild(row.data);
                        data = Array.isArray(children) ? children[+id[1]] : children;
                    } else {
                        data = row.data;
                    }
                    /**
                     * Emits the row data and the associated column component when a table cell is clicked.
                     * @event click
                     * @type {object}
                     */
                    this.$emit('click', {
                        row: data,
                        col: ci >= 0 ? this.col[ci] : void 0
                    });
                }
            },

            /** @private */
            before() {
                const start = { width: 0 };
                const arrow = { width: '20px', sticky: 'start', force: 'true' };
                return this.stickyStart >= 0 ? [ start, arrow ] : [ arrow ];
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tb {
        ._tbga {
            display: block;
            width: 20px;
            text-align: center;
            > svg {
                width: 12px;
                height: 12px;
                vertical-align: 0;
                transition: transform .15s linear;
            }
        }
        ._tbgch {
            display: none;
        }
        ._tbchid {
            border-bottom: none !important;
            background-image: none !important;
            &::after {
                content: '';
                position: absolute;
                top: 0;
                left: 50%;
                bottom: 0;
                border-right: 1px solid fade(@fg-accent, 20%);
            }
        }
        tr:first-child > ._tbchid {
            &::after {
                top: 8px;
            }
        }
        tr:last-child > ._tbchid {
            &::after {
                bottom: 8px;
            }
        }
        tr._trn {
            cursor: default;
        }
        ._tbgex {
            > tr > td {
                border-bottom: none;
                box-shadow: none;
            }
            ._tbga {
                display: inline-block;
                width: 20px;
                > svg {
                    transform: rotate(90deg);
                }
            }
            + ._tbgch {
                display: table-row-group;
            }
        }
    }
</style>

<ib-example id="basic" name="Basic, Download CSV">
    <template lang="pug">
        div
            ib-table-group(:data="positions", ref="groupDataTable")
                ib-table-col(width="12ch", sort="currency", sorted) Currency
                ib-table-col(width="12ch", sort="amount", numeric) Amount
                template(v-slot:body="pos, csv")
                    td(v-text="csv(pos.currency)")
                    td(v-num.fixed="csv(pos.amount, 'end')")
                template(v-slot:child="pos, csv")
                    td(colspan="2", v-text="csv(pos.note, 'end')")
            ib-button(@click="downloadCsv" class="before-16") Download CSV
    </template>
    <script>
        {
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                        { currency: 'GBP', amount: 35, note: 'Thirty-five British pounds' },
                        { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                        { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                        { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                        { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                        { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                        { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                        { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                    ]
                };
            },
            methods: {
                downloadCsv() {
                    this.$refs.groupDataTable.downloadCsvFile('grouped csv', 'Currency, Amount');
                }
            }

        }
    </script>
</ib-example>

<ib-example id="multi" name="Multiple Child Rows; Slide Buttons">
    <template lang="pug">
        ib-table-group(:data="positions", child="child", ref="groupDataTableTwo")
            ib-table-col(width="12ch", sort="currency", sorted) Currency
            ib-table-col(width="12ch", sort="amount", numeric) Amount
            template(v-slot:body="pos")
                td(v-text="pos.currency")
                td(v-num.fixed="pos.amount")
            template(v-slot:end="pos")
                ib-button(tone="buy", @click="buy(pos.currency)") Buy
                ib-button(tone="sell", @click="sell(pos.currency)") Sell
    </template>
    <script>
        {
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, child: [
                            { currency: 'EUR', amount: 600 },
                            { currency: 'AZN', amount: 40000 }
                        ] },
                        { currency: 'GBP', amount: 35, child: [
                            { currency: 'EUR', amount: 600 },
                            { currency: 'USD', amount: 20 }
                        ] },
                        { currency: 'NZD', amount: 12, child: [
                            { currency: 'EUR', amount: 600 },
                            { currency: 'USD', amount: 20 },
                            { currency: 'AZN', amount: 40000 }
                        ] },
                        { currency: 'EUR', amount: 600 },
                        { currency: 'AZN', amount: 40000 },
                        { currency: 'JPY', amount: 1000 },
                        { currency: 'AED', amount: 7500 },
                        { currency: 'CNY', amount: 12000 },
                        { currency: 'INR', amount: 400 }
                    ]
                };
            },
            methods: {
                buy(currency) {
                    this.$notify(this.$t('Buy {currency}', { currency }), { tone: 'ok', unique: true });
                },

                sell(currency) {
                    this.$notify(this.$t('Sell {currency}', { currency }), { tone: 'ok', unique: true });
                }

            }
        }
    </script>
</ib-example>
